import styled, { useTheme } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useGiftForm,
  STEP_NAMES,
  GiftFormProvider,
} from '../../../components/GiftFormContext';
import { ParagraphLargeText } from '../../../components/Texts';
import Steps from '../../../components/Steps';
import WidthContainer from '../../../components/WidthContainer';
import { hapticsImpactLight } from '../../../common/haptic';
import ChevronLeftIcon from '../../../assets/icons/chevron-left.svg?react';
import { useEffect, useRef, useState } from 'react';
import { PHONE_MEDIA_QUERY } from '../../../common/responsive';
import GiftIcon from '../../../assets/icons/gift.svg?react';
import {
  EVENT_GIFT_FLOW_DELIVERY_COMPLETED,
  EVENT_GIFT_FLOW_STARTED,
  sendEvent,
} from '../../../common/analytics';
import { debounce } from 'lodash';
import { useSelector } from 'react-redux';
import { selectPrivyToken } from '../../../redux/slices/auth';
import { useGetGiftsQuery } from '../../../redux/api/authorized/gifts';
import { useFlag } from '@unleash/proxy-client-react';

const TitleAndStepsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const Container = styled.div`
  width: 100%;
  max-width: 650px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px 0;

  @media ${PHONE_MEDIA_QUERY} {
    max-width: 100%;
  }
`;

const TitleHeader = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.text.title};
  cursor: pointer;
  font-size: 14px;
  padding: 8px;
  position: absolute;
  left: 0;
  &:hover {
    opacity: 0.7;
  }
`;

const FreeGiftBanner = styled.div`
  background-color: ${({ theme, isElegible }) =>
    isElegible ? theme.primary.ultraLight : theme.background.light};
  color: ${({ theme }) => theme.text.regular};
  padding: 10px;
  width: 100%;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 5px;
  max-width: 516px;
`;

const TitleText = styled(ParagraphLargeText)`
  text-align: center;
  width: 100%;
`;

const CreateGiftFlowContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const {
    currentStep,
    currentStepName,
    totalSteps,
    currentTitle,
    Component,
    onBackStepName,
    onNextStepName,
    shouldShowBackButton,
    buttonLabel,
    updateStepInUrl,
    getValues,
    isConfiguredGiftEligible,
    reason,
    showFreeGift,
    isBonusElegible,
    showBonus,
    sendingStatus,
    bonusReason,
    lpd,
  } = useGiftForm();

  const freeGiftAllInputs = useFlag('freeGiftFullInputs');

  const debouncedSendEvent = useRef(
    debounce(() => {
      sendEvent(EVENT_GIFT_FLOW_STARTED, {
        is_free_gift: showFreeGift,
        lpd: lpd,
        show_bonus: showBonus,
        is_bonus_eligible: isBonusElegible,
      });
    }, 300),
  ).current;

  useEffect(() => {
    debouncedSendEvent();
    return () => {
      debouncedSendEvent.cancel();
    };
  }, [debouncedSendEvent, showFreeGift, lpd, showBonus, isBonusElegible]);

  const handleBack = () => {
    hapticsImpactLight();
    if (onBackStepName) {
      updateStepInUrl(onBackStepName);
    } else {
      navigate(-1);
    }
  };

  const handleContinue = () => {
    // When skipping Delivery (lpd=yield), fire delivery completed event
    if (
      lpd === 'yield' &&
      currentStepName === STEP_NAMES.PREVIEW &&
      onNextStepName === STEP_NAMES.CONFIRMATION
    ) {
      sendEvent(EVENT_GIFT_FLOW_DELIVERY_COMPLETED, {
        delivery: getValues()?.delivery ?? 'email',
        is_free_gift: showFreeGift,
        free_gift_all_inputs: freeGiftAllInputs,
      });
    }
    if (onNextStepName) {
      updateStepInUrl(onNextStepName);
    }
  };

  const showBackButton =
    shouldShowBackButton &&
    !(lpd === 'yield' && currentStepName === STEP_NAMES.AMOUNT);

  return (
    <WidthContainer>
      <Container>
        <TitleAndStepsContainer>
          <TitleHeader>
            {showBackButton && (
              <BackButton onClick={handleBack}>
                <ChevronLeftIcon width={15} height={15} />
                <span>{t('common.back')}</span>
              </BackButton>
            )}
            <TitleText style={{ color: theme.text.title }}>
              {t(`giftFlow.stepTitles.${currentStepName}`, currentTitle) || currentTitle}
            </TitleText>
          </TitleHeader>
          <Steps
            steps={totalSteps}
            currentStep={
              lpd === 'yield'
                ? currentStep === totalSteps - 1
                  ? currentStep
                  : currentStep - 1
                : currentStep
            }
          />
          {showFreeGift && sendingStatus === 'idle' && lpd && (
            <FreeGiftBanner isElegible={isConfiguredGiftEligible}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <GiftIcon
                  width={20}
                  height={20}
                  style={{
                    color: theme.status.success.primary,
                    opacity: isConfiguredGiftEligible ? 1 : 0.5,
                  }}
                />
                <p
                  style={{
                    opacity: isConfiguredGiftEligible ? 1 : 0.5,
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(0.75rem, 1.5vw, 0.9rem)',
                  }}
                >
                  You are elegible for a{' '}
                  <span
                    style={{ fontWeight: 500, color: theme.primary.primary }}
                  >
                    free gift!
                  </span>
                </p>
              </div>
              <div>
                <p
                  style={{
                    fontSize: 12,
                    color: theme.text.secondary,
                    fontWeight: 300,
                  }}
                >
                  {reason ? reason : 'Keep these settings to send it for free!'}
                </p>
              </div>
            </FreeGiftBanner>
          )}
          {showBonus && sendingStatus === 'idle' && !lpd && !showFreeGift && (
            <FreeGiftBanner isElegible={isBonusElegible}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <GiftIcon
                  width={20}
                  height={20}
                  style={{
                    color: theme.status.success.primary,
                    opacity: isBonusElegible ? 1 : 0.5,
                  }}
                />
                <p
                  style={{
                    opacity: isBonusElegible ? 1 : 0.5,
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(0.75rem, 1.5vw, 0.9rem)',
                  }}
                >
                  You are elegible for a{' '}
                  <span
                    style={{ fontWeight: 500, color: theme.primary.primary }}
                  >
                    10%
                  </span>{' '}
                  PreStock Bonus
                </p>
              </div>
              <div>
                <p
                  style={{
                    fontSize: 12,
                    color: theme.text.secondary,
                    fontWeight: 300,
                  }}
                >
                  {bonusReason
                    ? bonusReason
                    : 'Keep these settings to apply the bonus!'}
                </p>
              </div>
            </FreeGiftBanner>
          )}
        </TitleAndStepsContainer>
        <Component
          isModal={false}
          isBack={false}
          currentStep={currentStep}
          onContinue={handleContinue}
          buttonLabel={t(`giftFlow.buttonLabels.${currentStepName}`, buttonLabel) || buttonLabel}
        />
      </Container>
    </WidthContainer>
  );
};

const CreateGiftFlow = () => {
  return (
    <GiftFormProvider>
      <CreateGiftFlowContent />
    </GiftFormProvider>
  );
};

export default CreateGiftFlow;
